import React from 'react'
import {
  Chevron,
  Content,
  Extra,
  Header,
  Item,
  Label,
  Panel,
  Root,
  Trigger,
} from 'sutra/components/Accordion/accordion-styles'
import type {
  AccordionIconPosition,
  AccordionSize,
  AccordionVariant,
} from 'sutra/components/Accordion/accordion-styles'
import { ChevronDown } from 'sutra/components/Icons'

export type {
  AccordionIconPosition,
  AccordionSize,
  AccordionVariant,
} from 'sutra/components/Accordion/accordion-styles'

export interface AccordionItem {
  // Identifies the item in `value`/`defaultValue`/`onValueChange`.
  value: string
  // The trigger's content. It sits inside a button, so it must be
  // non-interactive.
  header: React.ReactNode
  // A trailing summary inside the trigger (a count, a tag). Also
  // non-interactive: a button cannot hold another control.
  extra?: React.ReactNode
  children: React.ReactNode
}

export interface AccordionProps {
  items: AccordionItem[]
  // The open items. Omit it (and use `defaultValue`) for an uncontrolled
  // accordion.
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
  // Several items may be open at once. Defaults to true, which is antd's
  // `Collapse` and not Base UI's own default; pass `false` for antd's
  // `accordion`.
  multiple?: boolean
  // 'bordered' boxes the whole list, 'divided' keeps only the rules between
  // items, 'ghost' draws neither.
  variant?: AccordionVariant
  size?: AccordionSize
  iconPosition?: AccordionIconPosition
  // Keeps closed panels in the DOM (hidden) so their state survives a
  // close. By default a closed panel unmounts and a reopen remounts it.
  keepMounted?: boolean
  'aria-label'?: string
  className?: string
}

export const Accordion = ({
  items,
  value,
  defaultValue,
  onValueChange,
  multiple = true,
  variant = 'bordered',
  size = 'md',
  iconPosition = 'end',
  keepMounted,
  'aria-label': ariaLabel,
  className,
}: AccordionProps): React.ReactElement => (
  <Root
    $variant={variant}
    value={value}
    defaultValue={defaultValue}
    onValueChange={(next) => onValueChange?.(next)}
    multiple={multiple}
    keepMounted={keepMounted}
    aria-label={ariaLabel}
    className={className}
  >
    {items.map((item) => {
      const chevron = (
        <Chevron aria-hidden>
          <ChevronDown />
        </Chevron>
      )
      return (
        <Item key={item.value} value={item.value} $variant={variant}>
          <Header>
            <Trigger $variant={variant} $size={size}>
              {iconPosition === 'start' && chevron}
              <Label>{item.header}</Label>
              {item.extra !== undefined && <Extra>{item.extra}</Extra>}
              {iconPosition === 'end' && chevron}
            </Trigger>
          </Header>
          <Panel>
            <Content $variant={variant} $size={size}>
              {item.children}
            </Content>
          </Panel>
        </Item>
      )
    })}
  </Root>
)

Accordion.displayName = 'Accordion'
